import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldCheck, Send, MessageCircle, Clock, Heart,
  X, Lock, User, CheckCircle, HeartHandshake, Frown,
  Sparkles, Users, HelpCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { FooterSection } from "../components/landing/FooterSection";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: "love", label: "연애 고민", icon: Heart, color: "from-rose-500 to-pink-500", bg: "bg-rose-50", text: "text-rose-600", border: "border-rose-100" },
  { value: "breakup", label: "이별/재회", icon: Frown, color: "from-violet-500 to-purple-500", bg: "bg-violet-50", text: "text-violet-600", border: "border-violet-100" },
  { value: "marriage", label: "결혼/재혼", icon: HeartHandshake, color: "from-amber-500 to-orange-500", bg: "bg-amber-50", text: "text-amber-600", border: "border-amber-100" },
  { value: "loneliness", label: "외로움", icon: Users, color: "from-sky-500 to-blue-500", bg: "bg-sky-50", text: "text-sky-600", border: "border-sky-100" },
  { value: "self", label: "자기개발", icon: Sparkles, color: "from-emerald-500 to-teal-500", bg: "bg-emerald-50", text: "text-emerald-600", border: "border-emerald-100" },
  { value: "general", label: "기타", icon: HelpCircle, color: "from-stone-500 to-stone-600", bg: "bg-stone-50", text: "text-stone-600", border: "border-stone-100" },
];

export default function PrivateCounselingPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, token } = useAuth();
  const [categoryCounts, setCategoryCounts] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Write form state
  const [writeTitle, setWriteTitle] = useState("");
  const [writeContent, setWriteContent] = useState("");
  const [writeCategory, setWriteCategory] = useState("love");
  const [submitting, setSubmitting] = useState(false);
  const [showWriteSection, setShowWriteSection] = useState(true);

  // Confirmation popup
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [submittedName, setSubmittedName] = useState("");

  // My posts
  const [showMyPosts, setShowMyPosts] = useState(false);
  const [myPosts, setMyPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);

  // Admin reply
  const [replyContent, setReplyContent] = useState("");
  const [replying, setReplying] = useState(false);

  const isAdmin = user?.role === "admin";
  const isCoach = user?.membership_type === "premium" || user?.membership_type === "vip";

  const fetchCounts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/private-counseling/posts?limit=500`);
      if (res.ok) {
        const data = await res.json();
        setTotalCount(data.total || 0);
        const counts = {};
        (data.posts || []).forEach((p) => {
          counts[p.category] = (counts[p.category] || 0) + 1;
        });
        setCategoryCounts(counts);
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  const fetchMyPosts = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/private-counseling/my-posts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMyPosts(data.posts || []);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleViewDetail = async (postId) => {
    try {
      const url = (isAdmin || isCoach)
        ? `${API_URL}/api/private-counseling/posts/${postId}`
        : `${API_URL}/api/private-counseling/posts/${postId}`;
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(url, { headers });
      if (res.ok) {
        const data = await res.json();
        setSelectedPost(data);
        setReplyContent("");
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleReply = async () => {
    if (!replyContent.trim() || !selectedPost) return;
    setReplying(true);
    try {
      const res = await fetch(`${API_URL}/api/private-counseling/posts/${selectedPost.id}/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content: replyContent }),
      });
      if (res.ok) {
        setReplyContent("");
        handleViewDetail(selectedPost.id);
        fetchCounts();
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.detail || "답변 등록에 실패했습니다.");
      }
    } catch {
      alert("답변 등록에 실패했습니다.");
    }
    setReplying(false);
  };

  const handleSubmitPost = async () => {
    if (!writeTitle.trim() || !writeContent.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/private-counseling/posts`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ title: writeTitle, content: writeContent, category: writeCategory }),
      });
      if (res.ok) {
        const data = await res.json();
        setSubmittedName(data.anonymous_name || "익명");
        setShowWriteSection(false);
        setWriteTitle("");
        setWriteContent("");
        setWriteCategory("love");
        setShowConfirmPopup(true);
        fetchCounts();
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.detail || "등록에 실패했습니다.");
      }
    } catch {
      alert("등록에 실패했습니다.");
    }
    setSubmitting(false);
  };

  const getRelativeTime = (dateStr) => {
    if (!dateStr) return "";
    const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
    if (diff < 1) return "방금 전";
    if (diff < 60) return `${diff}분 전`;
    if (diff < 1440) return `${Math.floor(diff / 60)}시간 전`;
    return `${Math.floor(diff / 1440)}일 전`;
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero */}
      <section className="relative pt-24 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-stone-800 via-stone-900 to-stone-800" />
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <div className="w-16 h-16 bg-emerald-500/15 border border-emerald-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <ShieldCheck className="w-8 h-8 text-emerald-400" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">비공개 상담소</h1>
          <p className="text-stone-400 text-base sm:text-lg max-w-lg mx-auto leading-relaxed">
            당신의 고민은 안전하게 보호됩니다.<br />
            리즈 코치가 <span className="text-emerald-400 font-semibold">24시간 이내</span>에 답변해 드려요.
          </p>
          <div className="flex items-center justify-center gap-4 mt-6 text-sm text-stone-500">
            <span className="flex items-center gap-1.5"><Lock className="w-4 h-4" /> 완전 익명</span>
            <span className="w-px h-4 bg-stone-600" />
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> 24시간 내 답변</span>
            <span className="w-px h-4 bg-stone-600" />
            <span className="flex items-center gap-1.5"><MessageCircle className="w-4 h-4" /> 메시지함 전달</span>
          </div>
        </div>
      </section>

      {/* Category Count Cards */}
      <section className="max-w-4xl mx-auto px-6 -mt-8 relative z-10">
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            const count = categoryCounts[cat.value] || 0;
            return (
              <div
                key={cat.value}
                className={`${cat.bg} ${cat.border} border rounded-xl p-3 text-center transition-transform hover:scale-105`}
                data-testid={`category-count-${cat.value}`}
              >
                <Icon className={`w-5 h-5 ${cat.text} mx-auto mb-1.5`} />
                <p className={`text-xl font-bold ${cat.text}`}>{count}</p>
                <p className="text-[11px] text-stone-500 mt-0.5">{cat.label}</p>
              </div>
            );
          })}
        </div>
        <p className="text-center text-xs text-stone-400 mt-3">
          총 <span className="font-semibold text-stone-600">{totalCount}</span>건의 상담이 진행되었습니다
        </p>
      </section>

      {/* Action Links */}
      <section className="max-w-2xl mx-auto px-6 mt-8 flex items-center justify-center gap-3">
        {isAuthenticated && (
          <button
            onClick={() => { setShowMyPosts(!showMyPosts); if (!showMyPosts) fetchMyPosts(); }}
            className="text-sm text-stone-500 hover:text-stone-700 underline underline-offset-2 transition-colors"
            data-testid="my-posts-toggle"
          >
            {showMyPosts ? "돌아가기" : "내 상담 내역 보기"}
          </button>
        )}
        {(isAdmin || isCoach) && (
          <button
            onClick={() => navigate("/admin/counseling")}
            className="text-sm text-emerald-600 hover:text-emerald-700 underline underline-offset-2 transition-colors"
          >
            관리자 답변하기
          </button>
        )}
      </section>

      {/* Write Section (always visible) */}
      <section className="max-w-2xl mx-auto px-6 mt-6">
        <div className="bg-white rounded-2xl border border-stone-200 shadow-lg overflow-hidden">
          <div className="bg-stone-800 p-5">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-5 h-5 text-emerald-400" />
              <h3 className="font-bold text-white">상담 신청하기</h3>
            </div>
          </div>

              <div className="p-5 space-y-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-start gap-2.5">
                  <Lock className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-emerald-700 leading-relaxed">
                    작성자 정보는 완전히 비공개됩니다. 다른 회원에게 고민 내용이 노출되지 않으며, 코치만 확인할 수 있습니다.
                  </p>
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">고민 카테고리</label>
                  <div className="grid grid-cols-3 gap-2">
                    {CATEGORIES.map((cat) => {
                      const Icon = cat.icon;
                      const isActive = writeCategory === cat.value;
                      return (
                        <button
                          key={cat.value}
                          onClick={() => setWriteCategory(cat.value)}
                          className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                            isActive
                              ? `${cat.bg} ${cat.border} ${cat.text} ring-2 ring-offset-1 ring-stone-300`
                              : "bg-white border-stone-200 text-stone-500 hover:bg-stone-50"
                          }`}
                          data-testid={`write-category-${cat.value}`}
                        >
                          <Icon className="w-4 h-4" />
                          {cat.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">제목</label>
                  <input
                    type="text"
                    value={writeTitle}
                    onChange={(e) => setWriteTitle(e.target.value)}
                    placeholder="고민의 제목을 입력하세요"
                    className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-stone-300 focus:border-transparent"
                    maxLength={100}
                    data-testid="counseling-title-input"
                  />
                </div>

                {/* Content */}
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">고민 내용</label>
                  <textarea
                    value={writeContent}
                    onChange={(e) => setWriteContent(e.target.value)}
                    placeholder="편하게 고민을 적어주세요. 구체적으로 작성하실수록 더 정확한 답변을 받으실 수 있어요."
                    className="w-full h-44 px-4 py-3 border border-stone-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-stone-300 focus:border-transparent"
                    maxLength={2000}
                    data-testid="counseling-content-textarea"
                  />
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-xs text-stone-400">{writeContent.length}/2,000</span>
                    <span className="text-xs text-emerald-500 flex items-center gap-1"><Clock className="w-3 h-3" /> 24시간 이내 답변</span>
                  </div>
                </div>
              </div>

              <div className="p-5 pt-0">
                <Button
                  className="w-full py-4 text-base bg-gradient-to-r from-stone-800 to-stone-700 hover:from-stone-700 hover:to-stone-600 text-white font-bold rounded-xl shadow-md"
                  onClick={() => {
                    if (!isAuthenticated) { navigate("/"); return; }
                    handleSubmitPost();
                  }}
                  disabled={submitting || !writeTitle.trim() || !writeContent.trim()}
                  data-testid="submit-counseling-btn"
                >
                  <Send className="w-5 h-5 mr-2" />
                  {submitting ? "등록 중..." : "고민 올리기"}
                </Button>
              </div>

              {/* 리즈 상담 안내 */}
              <div className="px-5 pb-5">
                <div
                  className="bg-gradient-to-r from-rose-50 to-pink-50 border border-rose-200 rounded-xl p-4 flex items-center justify-between cursor-pointer hover:from-rose-100 hover:to-pink-100 transition-colors"
                  onClick={() => navigate("/counseling")}
                  data-testid="liz-counseling-banner"
                >
                  <div>
                    <p className="text-sm font-semibold text-rose-700">더 자세한 1:1 상담이 필요하신가요?</p>
                    <p className="text-xs text-rose-500 mt-0.5">리즈 코치와 프라이빗 상담을 받아보세요</p>
                  </div>
                  <span className="text-xs bg-rose-500 text-white px-3 py-1.5 rounded-full font-medium whitespace-nowrap">리즈 상담 신청 &rarr;</span>
                </div>
              </div>
            </div>
          </section>

      {/* My Posts Section (private to user) */}
      <AnimatePresence>
        {showMyPosts && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="max-w-2xl mx-auto px-6 mt-8"
          >
            <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
              <div className="p-5 border-b border-stone-100">
                <h3 className="font-bold text-stone-800 flex items-center gap-2">
                  <User className="w-5 h-5 text-stone-500" />
                  내 상담 내역
                </h3>
                <p className="text-xs text-stone-400 mt-1">나만 볼 수 있는 비공개 내역입니다</p>
              </div>
              {myPosts.length === 0 ? (
                <div className="p-10 text-center text-stone-400 text-sm">아직 등록한 상담이 없습니다.</div>
              ) : (
                <div className="divide-y divide-stone-50">
                  {myPosts.map((post) => (
                    <div
                      key={post.id}
                      className="p-4 hover:bg-stone-50 cursor-pointer transition-colors"
                      onClick={() => handleViewDetail(post.id)}
                      data-testid={`my-post-${post.id}`}
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-stone-100 text-stone-500">
                          {CATEGORIES.find(c => c.value === post.category)?.label || "기타"}
                        </span>
                        <span className={`text-[11px] px-2 py-0.5 rounded-full ${
                          post.status === "answered" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                        }`}>
                          {post.status === "answered" ? "답변 완료" : "답변 대기"}
                        </span>
                      </div>
                      <h4 className="font-medium text-stone-800 text-sm">{post.title}</h4>
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-stone-400">
                        <span>{getRelativeTime(post.created_at)}</span>
                        <span>답변 {post.replies_count || 0}건</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      {/* Spacer */}
      <div className="h-20" />

      <FooterSection />

      {/* 24시간 이내 답변 확인 팝업 */}
      <AnimatePresence>
        {showConfirmPopup && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setShowConfirmPopup(false)}
          >
            <motion.div
              initial={{ scale: 0.85, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.85, opacity: 0, y: 30 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
              className="bg-white rounded-2xl max-w-sm w-full shadow-2xl overflow-hidden text-center"
              onClick={(e) => e.stopPropagation()}
              data-testid="confirm-popup"
            >
              <div className="bg-gradient-to-b from-emerald-500 to-emerald-600 pt-8 pb-6 px-6">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <CheckCircle className="w-9 h-9 text-emerald-500" />
                </div>
                <h3 className="text-xl font-bold text-white">고민이 접수되었습니다</h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="bg-stone-50 rounded-xl p-4">
                  <p className="text-sm text-stone-600">
                    <span className="font-semibold text-stone-800">{submittedName}</span>님의 고민이
                  </p>
                  <p className="text-sm text-stone-600">안전하게 등록되었습니다.</p>
                </div>

                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Clock className="w-5 h-5 text-emerald-600" />
                    <span className="font-bold text-emerald-700 text-lg">24시간 이내</span>
                  </div>
                  <p className="text-sm text-emerald-700">리즈 코치가 답변해 드립니다.</p>
                  <p className="text-xs text-emerald-600 mt-1">답변은 <strong>메시지함</strong>으로 전달됩니다.</p>
                </div>

                <Button
                  className="w-full py-3 bg-stone-800 hover:bg-stone-900 text-white font-bold rounded-xl"
                  onClick={() => setShowConfirmPopup(false)}
                  data-testid="confirm-popup-close"
                >
                  확인
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Detail Modal (for my posts & admin) */}
      <AnimatePresence>
        {selectedPost && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setSelectedPost(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-stone-800 p-5 text-white relative">
                <button
                  onClick={() => setSelectedPost(null)}
                  className="absolute top-4 right-4 p-1.5 hover:bg-white/10 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-white/20">
                    {CATEGORIES.find(c => c.value === selectedPost.category)?.label || "기타"}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    selectedPost.status === "answered" ? "bg-emerald-500/30 text-emerald-200" : "bg-amber-500/30 text-amber-200"
                  }`}>
                    {selectedPost.status === "answered" ? "답변 완료" : "답변 대기"}
                  </span>
                </div>
                <h3 className="text-lg font-bold">{selectedPost.title}</h3>
                <p className="text-sm text-stone-400 mt-1">{selectedPost.anonymous_name} &middot; {getRelativeTime(selectedPost.created_at)}</p>
              </div>

              <div className="p-5">
                <div className="bg-stone-50 rounded-xl p-4 mb-5">
                  <p className="text-sm text-stone-700 leading-relaxed whitespace-pre-wrap">{selectedPost.content}</p>
                </div>

                {/* Replies */}
                {selectedPost.replies && selectedPost.replies.length > 0 && (
                  <div className="space-y-3 mb-5">
                    <h4 className="font-semibold text-stone-800 text-sm flex items-center gap-1.5">
                      <MessageCircle className="w-4 h-4 text-emerald-500" />
                      코치 답변 ({selectedPost.replies.length})
                    </h4>
                    {selectedPost.replies.map((reply) => (
                      <div key={reply.id} className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center">
                            <CheckCircle className="w-4 h-4 text-white" />
                          </div>
                          <span className="text-sm font-semibold text-emerald-800">{reply.coach_name}</span>
                          <span className="text-xs text-stone-400">{getRelativeTime(reply.created_at)}</span>
                        </div>
                        <p className="text-sm text-stone-700 leading-relaxed whitespace-pre-wrap">{reply.content}</p>
                      </div>
                    ))}
                  </div>
                )}

                {selectedPost.status === "open" && !(isAdmin || isCoach) && (
                  <div className="text-center py-4">
                    <Clock className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                    <p className="text-sm text-stone-500">코치가 답변을 준비 중입니다.</p>
                    <p className="text-xs text-stone-400 mt-1">24시간 이내에 메시지함으로 답변이 전달됩니다.</p>
                  </div>
                )}

                {/* Admin/Coach Reply Form */}
                {(isAdmin || isCoach) && (
                  <div className="border-t border-stone-100 pt-4">
                    <h4 className="text-sm font-semibold text-stone-700 mb-2">답변 작성</h4>
                    <textarea
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      placeholder="따뜻한 답변을 작성해주세요. 답변은 작성자의 메시지함으로 전달됩니다."
                      className="w-full h-28 px-3 py-2.5 border border-stone-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-300"
                      data-testid="reply-textarea"
                    />
                    <div className="flex justify-end mt-2">
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={handleReply}
                        disabled={replying || !replyContent.trim()}
                        data-testid="submit-reply-btn"
                      >
                        <Send className="w-4 h-4 mr-1.5" />
                        {replying ? "전송 중..." : "답변 보내기"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
